import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, DUCK_LOGO } from "@/lib/api";
import { LogOut, Users, LayoutDashboard, UserCog, Sparkles, Rocket, CalendarDays, ExternalLink, Settings, MessageCircle, CalendarClock, MessageSquare, CalendarRange, ArrowLeft, Compass, Radio, TrendingUp, PlusCircle, ClipboardList, Landmark, Target, Gift, ChevronDown, ChevronRight, Gamepad2, Plug, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/app/NotificationBell";
import MobileBottomNav from "@/components/app/MobileBottomNav";
import AdminDucky from "@/components/app/AdminDucky";
import TickerTape from "@/components/app/TickerTape";
import { launchWalkthrough } from "@/components/app/WalkthroughTour";
import Avatar from "@/components/app/Avatar";
import AccountSwitcher, { PersonalModeBanner } from "@/components/app/AccountSwitcher";

export default function AppShell({ children, sidebar = true }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // ---- Work-queue pulse tracker (staff + tradeline_admin only) ----
  // Poll every 25s so the sidebar dot reflects live queue health without SSE plumbing.
  //   • queued > 0 → red BLINK (emergency, nobody has picked it up)
  //   • in_progress > 0 → green (someone's on it)
  //   • else → red (no active work)
  const [wqPulse, setWqPulse] = useState({ credit_report: null, tradeline: null });
  useEffect(() => {
    const role = user?.role;
    const kinds = role === "tradeline_admin" ? ["tradeline"]
      : ["super_admin", "admin", "credit_specialist", "team_member"].includes(role) ? ["credit_report"]
      : [];
    if (kinds.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      for (const kind of kinds) {
        try {
          const { data } = await api.get(`/admin/work-queue?kind=${kind}`);
          const items = data?.items || [];
          const queued = items.filter((i) => i.status === "queued").length;
          const in_progress = items.filter((i) => i.status === "in_progress").length;
          if (cancelled) return;
          setWqPulse((prev) => ({ ...prev, [kind]: { queued, in_progress } }));
        } catch { /* silent */ }
      }
    };
    tick();
    const t = setInterval(tick, 25000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role]);

  const wqLinkKind = ({
    "/admin/work-queue": "credit_report",
    "/tradeline-admin":  "tradeline",
  });
  const pulseFor = (to) => {
    const kind = wqLinkKind[to];
    const p = kind && wqPulse[kind];
    if (!p) return null;
    if (p.queued > 0)      return { tone: "red-blink", count: p.queued };
    if (p.in_progress > 0) return { tone: "green",     count: p.in_progress };
    return { tone: "red-idle", count: 0 };
  };

  // ---- Partner presence tracker (admins only) ----
  // Small "Marke online / 12m ago" badge shown next to the Tradelines link.
  const [partnerStatus, setPartnerStatus] = useState({});
  useEffect(() => {
    if (!["super_admin", "admin"].includes(user?.role)) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/presence/role-status?role=tradeline_admin`);
        if (!cancelled) setPartnerStatus((p) => ({ ...p, tradeline_admin: data }));
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role]);

  // ---- Live headcount tracker — clients + team on the site right now ----
  const [liveCounts, setLiveCounts] = useState({ clients: 0, staff: 0, guests: 0 });
  useEffect(() => {
    if (!["super_admin", "admin", "credit_specialist", "team_member"].includes(user?.role)) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/presence/live`);
        if (cancelled) return;
        const users = data?.users || [];
        setLiveCounts({
          clients: users.filter((u) => u.role === "client").length,
          staff:   users.filter((u) => u.role !== "client").length,
          guests:  data?.guests || 0,
        });
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role]);

  // ---- Live Video Room presence — which rooms are live + who's in them ----
  const [videoLive, setVideoLive] = useState({ count: 0, items: [] });
  useEffect(() => {
    if (!["super_admin", "admin", "credit_specialist", "team_member"].includes(user?.role)) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/warroom/live`);
        if (!cancelled) setVideoLive({ count: data?.count || 0, items: data?.items || [] });
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 6000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role]);

  const displayRole = {
    super_admin: "super admin", admin: "admin", credit_specialist: "credit specialist",
    team_member: "team member", client: "client",
  }[user?.role] || user?.role;

  // Where "home" should route to for THIS user — never the marketing site.
  const homePath = ["super_admin", "admin", "credit_specialist", "team_member"].includes(user?.role)
    ? "/admin"
    : user?.role === "tradeline_admin"
    ? "/tradeline-admin"
    : user?.role === "client"
    ? "/portal"
    : "/";

  // Show a "Back" affordance whenever the user is not already on their home.
  const canGoBack = !!user && loc.pathname !== homePath;

  const links = ["super_admin", "admin"].includes(user?.role)
    ? [
        { to: "/admin", label: "Clients", icon: LayoutDashboard },
        { to: "/admin/work-queue", label: "Work Queue", icon: ClipboardList },
        { to: "/admin/live", label: "Live Now", icon: Radio, live: true },
        { to: "/admin/warroom", label: "Video Room", icon: Video },
        { to: "/tradeline-admin", label: "Tradelines", icon: Landmark, partnerRole: "tradeline_admin" },
        {
          groupKey: "messaging", label: "Messaging & Booking", icon: MessageSquare,
          sublinks: [
            { to: "/admin/sms",           label: "SMS Inbox",     icon: MessageSquare },
            { to: "/admin/bulk-schedule", label: "Bulk Booking",  icon: CalendarRange },
            { to: "/admin/meetings",      label: "Meetings",      icon: CalendarDays },
          ],
        },
        { to: "/admin/users", label: "Team & Users", icon: UserCog },
        { to: "/admin/feed", label: "Money News Today", icon: Sparkles },
        { to: "/admin/service-requests", label: "Service Requests", icon: ClipboardList },
        { to: "/admin/goal-insights", label: "Goal Insights", icon: Target },
        { to: "/admin/partner-leads", label: "Partner Leads", icon: Target },
        {
          groupKey: "settings", label: "Settings", icon: Settings,
          sublinks: [
            { to: "/profile",         label: "My Profile",         icon: UserCog },
            { to: "/admin/settings",  label: "Automations",        icon: Settings },
            { to: "/admin/ticker",    label: "Ticker Tape",        icon: TrendingUp },
            ...(user?.role === "super_admin" ? [{ to: "/admin/integrations", label: "API & Integrations", icon: Plug }] : []),
          ],
        },
      ]
    : user?.role === "tradeline_admin"
    ? [
        { to: "/tradeline-admin", label: "Tradeline Ops", icon: Landmark },
        { to: "/profile", label: "My Profile", icon: UserCog },
      ]
    : ["credit_specialist", "team_member"].includes(user?.role)
    ? [
        { to: "/admin", label: "Clients", icon: LayoutDashboard },
        { to: "/admin/work-queue", label: "Work Queue", icon: ClipboardList },
        { to: "/admin/live", label: "Live Now", icon: Radio, live: true },
        { to: "/admin/warroom", label: "Video Room", icon: Video },
        {
          groupKey: "messaging", label: "Messaging & Booking", icon: MessageSquare,
          sublinks: [
            { to: "/admin/sms",           label: "SMS Inbox",    icon: MessageSquare },
            { to: "/admin/bulk-schedule", label: "Bulk Booking", icon: CalendarRange },
            { to: "/admin/meetings",      label: "Meetings",     icon: CalendarDays },
          ],
        },
        { to: "/admin/service-requests", label: "Service Requests", icon: ClipboardList },
        { to: "/portal/referrals", label: "Refer & Earn", icon: Gift },
        { to: "/admin/feed", label: "Money News Today", icon: Sparkles },
        { to: "/profile", label: "My Profile", icon: UserCog },
      ]
    : [
        { to: "/portal", label: "My Progress", icon: LayoutDashboard },
        { to: "/portal/services", label: "Start New Services", icon: PlusCircle },
        { to: "/portal/tradelines", label: "Buy Tradelines", icon: Landmark },
        { to: "/portal/messages", label: "Messages", icon: MessageCircle },
        { to: "/portal/meetings", label: "Meetings", icon: CalendarDays },
        { to: "/portal/funding", label: "My Funding Center", icon: Rocket },
        { to: "/portal/referrals", label: "Refer & Earn", icon: Gift },
        { to: "/portal/feed", label: "Money News Today", icon: Sparkles },
        { to: "/profile", label: "My Profile", icon: UserCog },
      ];

  return (
    <div className="min-h-screen bg-slate-50">
      <PersonalModeBanner />
      {/* NYSE-style live ticker tape */}
      <TickerTape />
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {canGoBack && (
              <button
                onClick={() => { if (window.history.length > 1) nav(-1); else nav(homePath); }}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition"
                title="Back"
                aria-label="Back"
                data-testid="app-back-btn"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <Link to={homePath} className="flex items-center gap-2 min-w-0" data-testid="brand-link">
              <img src={DUCK_LOGO} alt="Credit Quack" className="h-10 w-10 object-contain cq-waddle" />
              <div className="leading-tight hidden sm:block">
                <p className="font-display font-black text-slate-900 text-lg">Credit Quack</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Client Tracker</p>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <AccountSwitcher />
            {user && <NotificationBell enabled={!!user} />}
            {user && (
              <Avatar
                src={user.avatar_url}
                name={user.name}
                size="md"
                tone={user.role === "client" ? "sky" : "amber"}
                ringed
                testid="user-avatar"
              />
            )}
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-slate-800" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.title || displayRole}</p>
            </div>
            <Button variant="outline" size="sm" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn">
              <LogOut size={16} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex gap-6 pb-24 md:pb-8">
        {sidebar && (
          <aside className="hidden md:block w-56 shrink-0">
            <nav className="sticky top-24 space-y-1">
              {links.map((l) => {
                // ---- Grouped item (collapsible dropdown) ----
                if (l.sublinks && Array.isArray(l.sublinks)) {
                  return (
                    <NavGroup key={l.groupKey || l.label} group={l} pathname={loc.pathname} />
                  );
                }
                // ---- Flat item ----
                const active = loc.pathname === l.to || (l.to !== "/" && loc.pathname.startsWith(l.to));
                const Icon = l.icon;
                const pulse = pulseFor(l.to);
                const partner = l.partnerRole ? partnerStatus[l.partnerRole] : null;
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition ${
                      active ? "bg-amber-50 text-amber-700" : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    title={partner ? partnerLabel(partner) : undefined}
                  >
                    <Icon size={18} className="shrink-0" />
                    <span className="flex-1 min-w-0 flex flex-col">
                      <span className="truncate leading-tight">{l.label}</span>
                      {partner && <PartnerStatusPill data={partner} />}
                    </span>
                    {l.live && (
                      <span className="ml-auto flex items-center gap-1.5 shrink-0">
                        {liveCounts.clients > 0 && (
                          <span
                            className="text-[10px] font-black rounded-full bg-emerald-500 text-white px-1.5 min-w-4 h-4 inline-flex items-center justify-center animate-[cq-pulse_2.5s_ease-in-out_infinite]"
                            title={`${liveCounts.clients} client${liveCounts.clients === 1 ? "" : "s"} online now`}
                            data-testid="live-client-count"
                          >
                            {liveCounts.clients}
                          </span>
                        )}
                        <span className="relative flex h-2.5 w-2.5" aria-label="live">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                        </span>
                      </span>
                    )}
                    {!l.live && pulse && (
                      <QueuePulseDot pulse={pulse} testid={`wq-pulse-${l.to.replace(/[^a-z]/gi, "-")}`} />
                    )}
                    {l.to === "/admin/warroom" && videoLive.count > 0 && (
                      <span className="ml-auto flex items-center gap-1.5 shrink-0" data-testid="video-live-badge"
                        title={videoLive.items.map((r) => `${r.title}: ${(r.participants || []).join(", ")}`).join(" · ")}>
                        <span className="text-[9px] font-black tracking-wide rounded-full bg-emerald-500 text-white px-1.5 h-4 inline-flex items-center animate-[cq-pulse_2.5s_ease-in-out_infinite]">
                          LIVE{videoLive.count > 1 ? ` ${videoLive.count}` : ""}
                        </span>
                        <span className="relative flex h-2.5 w-2.5" aria-label="live">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                        </span>
                      </span>
                    )}
                  </Link>
                );
              })}
              <a
                href="https://www.CreditQuack.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-amber-700 bg-amber-50/60 hover:bg-amber-100 transition mt-4"
                data-testid="nav-official-site"
              >
                <ExternalLink size={18} /> Credit Quack Website
              </a>
              {user?.role === "client" && (
                <Link
                  to="/city"
                  onClick={() => { try { localStorage.setItem("cq_game_mode", "1"); } catch {} }}
                  className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold text-cyan-100 bg-slate-950 hover:bg-slate-900 border border-cyan-400/40 transition mt-2 shadow-md"
                  data-testid="nav-game-mode"
                >
                  <Gamepad2 size={18} className="text-cyan-300" /> Game Mode · Quack City
                </Link>
              )}
              {user?.role === "client" && (
                <button
                  type="button"
                  onClick={launchWalkthrough}
                  className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 transition mt-2 shadow-md shadow-amber-200"
                  data-testid="nav-walkthrough"
                >
                  <Compass size={18} /> Interactive Walkthrough
                </button>
              )}
            </nav>
          </aside>
        )}
        <main className="flex-1 min-w-0">{children}</main>
      </div>

      {/* Mobile sticky bottom navigation (client role only) */}
      <MobileBottomNav role={user?.role} />

      {/* Ducky — AI admin assistant (staff/admin only) */}
      {["super_admin", "admin", "credit_specialist", "team_member"].includes(user?.role) && <AdminDucky />}
    </div>
  );
}


function QueuePulseDot({ pulse, testid }) {
  if (!pulse) return null;
  const tone = pulse.tone;
  // Colors:
  //   red-blink → someone waiting; emergency pulse
  //   green     → someone in progress; steady
  //   red-idle  → nothing in progress AND nothing queued; steady dull red
  const dotClass =
    tone === "green"      ? "bg-emerald-500"
    : tone === "red-blink" ? "bg-rose-600"
    : "bg-rose-400 opacity-70";
  const ping = tone === "red-blink"
    ? "animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-80"
    : "";
  return (
    <span
      className="relative flex h-2.5 w-2.5 ml-auto"
      title={
        tone === "red-blink" ? `${pulse.count} waiting — pick one up`
        : tone === "green"    ? `${pulse.count} in progress`
        : "No active work"
      }
      data-testid={testid}
    >
      {ping && <span className={ping} />}
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${dotClass}`} />
      {pulse.count > 0 && tone === "red-blink" && (
        <span className="absolute -top-1.5 -right-2 h-3.5 min-w-3.5 px-1 rounded-full bg-rose-600 text-[9px] font-black text-white flex items-center justify-center leading-none">
          {pulse.count}
        </span>
      )}
    </span>
  );
}

function NavGroup({ group, pathname }) {
  const Icon = group.icon;
  const storageKey = `cq_nav_group_${group.groupKey || group.label}`;
  const childActive = (group.sublinks || []).some(
    (s) => pathname === s.to || (s.to !== "/" && pathname.startsWith(s.to)),
  );
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return childActive;
    const saved = localStorage.getItem(storageKey);
    if (saved === "1") return true;
    if (saved === "0") return false;
    return childActive;
  });
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch {}
  };
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="flex flex-col" data-testid={`nav-group-${group.groupKey || group.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={`group flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold tracking-tight transition select-none ${
          childActive
            ? "text-amber-700 bg-amber-50/60"
            : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/70"
        }`}
      >
        <Icon size={16} className="shrink-0 opacity-80" />
        <span className="flex-1 text-left truncate">{group.label}</span>
        <Chevron
          size={14}
          className={`shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
        />
      </button>
      {open && (
        <div className="ml-4 pl-3 border-l border-slate-200/70 space-y-0.5 mt-1 mb-1">
          {group.sublinks.map((s) => {
            const SubIcon = s.icon;
            const active = pathname === s.to || (s.to !== "/" && pathname.startsWith(s.to));
            return (
              <Link
                key={s.to}
                to={s.to}
                className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium tracking-tight transition ${
                  active
                    ? "bg-amber-50 text-amber-700 font-semibold"
                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100/70"
                }`}
                data-testid={`nav-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <SubIcon size={13} className="opacity-70" />
                <span className="truncate">{s.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}



function partnerFirstName(name) {
  if (!name) return "Partner";
  return name.replace(/\s*\(.*\)\s*$/, "").split(" ")[0];
}

function partnerAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!then) return null;
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function partnerLabel(data) {
  const first = partnerFirstName(data?.name);
  const ago = partnerAgo(data?.last_seen_iso);
  if (data?.online) return `${first} is online right now${ago ? ` — last active ${ago}` : ""}`;
  return ago ? `${first} · last seen ${ago}` : `${first} has never signed in`;
}

function PartnerStatusPill({ data }) {
  const first = partnerFirstName(data?.name);
  const ago = partnerAgo(data?.last_seen_iso);
  if (data?.online) {
    return (
      <span
        className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 whitespace-nowrap"
        data-testid={`partner-status-online-${(data?.role || "").replace(/_/g, "-")}`}
      >
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </span>
        {first} online now{ago ? ` · seen ${ago}` : ""}
      </span>
    );
  }
  return (
    <span
      className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 whitespace-nowrap"
      data-testid={`partner-status-offline-${(data?.role || "").replace(/_/g, "-")}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
      {ago ? `${first} · last seen ${ago}` : `${first} · never online`}
    </span>
  );
}
